const SEPARATOR = ' ';
const CUSTOM_CLASS = 'nab-focused-element';

export function getCssPath(
	element: Element | null | undefined,
	mode: 'single' | 'multiple' = 'single'
): string | undefined {
	if ( ! element ) {
		return undefined;
	}
	element.classList.add( CUSTOM_CLASS );
	const selector = getSelectorTraversingParents( element, mode );
	element.classList.remove( CUSTOM_CLASS );
	return maybeAddTag( element.nodeName, selector );
}

// =======
// HELPERS
// =======

/**
 * Retrieves the best CSS selector path for an element by traversing its parent
 * elements.
 *
 * This function analyzes the element's hierarchy and assigns a selector that
 * uniquely identifies it. It prioritizes class names and IDs while avoiding
 * structural selectors.
 *
 * @param {Element} element - The element to analyze.
 * @param {string}  mode    - The mode of selection, either 'single' or 'multiple'.
 * @return {string | undefined} - The computed selector path or undefined if no valid selector is found.
 */
function getSelectorTraversingParents(
	element: Element,
	mode: 'single' | 'multiple'
): string | undefined {
	if ( ! element ) {
		return;
	}

	const tagName = element.tagName;

	// Exclude unsupported or root tags
	if ( isUndefined( tagName ) || tagName === 'HTML' ) {
		return;
	}

	// Directly return if the element is 'body'
	if ( tagName === 'BODY' ) {
		return 'body';
	}

	// Get the element's best selector
	const bestSelector = determineBestSelector( element, mode );

	// Return if the best selector is an ID-based selector
	if ( /#/g.test( bestSelector ) ) {
		return bestSelector;
	}

	// Gather parent elements up to 'body'
	const parentElements: Element[] = [];
	let parent = element.parentElement;
	while ( parent && parent.tagName !== 'BODY' ) {
		parentElements.push( parent );
		parent = parent.parentElement;
	}

	// Determine if resetting should be skipped
	let selector = '';
	let currentSelector = '';
	let previousSelector = '';
	let selectedElementsCount;
	const resetSelectors: string[] = [];

	// Traverse parent elements from innermost to outermost
	for ( let i = parentElements.length - 1; i >= 0; i-- ) {
		currentSelector = determineBestSelector(
			parentElements[ i ] as Element,
			mode
		);

		let resetPerformed = false;
		if ( /#|\./g.test( currentSelector ) ) {
			if ( querySelectorAll( currentSelector ).length === 1 ) {
				if (
					mode !== 'single' ||
					! singleSelector( selector, true ).includes( 'nth-child' )
				) {
					selector = currentSelector + SEPARATOR;
					resetSelectors.push( currentSelector );
					resetPerformed = true;
				}
			}
		}

		if ( ! resetPerformed ) {
			selectedElementsCount = querySelectorAll(
				`${ selector } ${ currentSelector } ${ currentSelector },${ selector } ${ previousSelector } ${ currentSelector }`
			).length;

			if (
				mode === 'multiple' &&
				selectedElementsCount > 0 &&
				spaceCleaner( selector ).trim() !== ''
			) {
				selector = `${ spaceCleaner(
					selector
				).trim() } > ${ currentSelector } `;
			} else {
				selector += currentSelector + SEPARATOR;
			}
		}

		previousSelector = currentSelector;
	}

	// Handle Google Maps content cleanup
	if ( selector.includes( '.gm-style' ) ) {
		selector = '.gm-style';
	}

	// Finalize selector with the last determined selector
	selectedElementsCount = querySelectorAll(
		`${ selector } ${ bestSelector } ${ bestSelector },${ selector } ${ currentSelector } ${ bestSelector }`
	).length;
	selector = spaceCleaner(
		selectedElementsCount > 0 && selector.length
			? `${ selector } > ${ bestSelector }`
			: `${ selector } ${ bestSelector }`
	);

	// Optimize selector if multiple reset indexes exist and the selector is not ID-based
	if ( resetSelectors.length > 1 && selector.charAt( 0 ) !== '#' ) {
		// Attempt to eliminate structural selectors
		const newReset1: string[] = [];
		let isValid;
		let regex;
		for ( let i = 0; i < resetSelectors.length; i++ ) {
			isValid = true;

			for ( let j = 0; j < unPreferedSelectors.length; j++ ) {
				regex = new RegExp(
					'(\\s|^).' + unPreferedSelectors[ j ] + '(\\s|$)',
					'gi'
				);
				if ( regex.test( resetSelectors[ i ] as string ) ) {
					isValid = false;
					break;
				}
			}

			if ( isValid ) {
				newReset1.push( resetSelectors[ i ] as string );
			}
		}

		// Further filter reset selectors if multiple remain
		const newReset2: string[] = [];
		if ( newReset1.length > 1 ) {
			let depthArray: number[];
			let tagArray: string[];
			for ( let i = 0; i < newReset1.length; i++ ) {
				depthArray = [];
				tagArray = [];

				document
					.querySelectorAll( newReset1[ i ] as string )
					.forEach( ( currentElement ) => {
						depthArray.push(
							currentElement.parentElement?.parentElement
								?.children.length || 0
						);
						tagArray.push( currentElement.nodeName );
					} );

				const depthConsistent = depthArray.every(
					( v, _, a ) => v === a[ 0 ]
				);
				const tagConsistent = tagArray.every(
					( v, _, a ) => v === a[ 0 ]
				);

				if ( depthConsistent && tagConsistent ) {
					newReset2.push( newReset1[ i ] as string );
				}
			}
		}

		let optimizedSelector = null;

		// Determine the best reset selector
		if ( newReset2.length > 0 ) {
			optimizedSelector = newReset2[ newReset2.length - 1 ];
		} else if ( newReset1.length > 0 ) {
			optimizedSelector = newReset1[ newReset1.length - 1 ];
		}

		// Update the selector if a valid optimization is found
		if ( optimizedSelector !== null ) {
			let newSelector = ( selector.match( /(\s)(.*?)$/g ) || [] )
				.join( '' )
				.toString();

			newSelector = optimizedSelector + newSelector;

			if ( checkSelector( newSelector, true ) ) {
				if ( comparisonSelector( selector, newSelector ) ) {
					selector = newSelector;
				}
			}
		}
	}

	// Return the single selector if in 'sharp' mode
	if ( mode === 'single' ) {
		return singleSelector( selector, false );
	}

	// Check if all other elements have the same tag name
	if ( simpleLikedTags.includes( tagName.toLowerCase() ) ) {
		const foundTags: string[] = [];
		querySelectorAll( selector ).forEach( ( node ) => {
			const nodeName = node.nodeName;

			if ( ! foundTags.includes( nodeName ) ) {
				foundTags.push( nodeName );
			}
		} );

		// Use the tag name if multiple tags are found
		if ( foundTags.length > 1 ) {
			selector =
				(
					selector.match(
						new RegExp(
							'^(.*?)' + selectorRegex( bestSelector ) + '$',
							'g'
						)
					) || []
				)
					.join( '' )
					.toString()
					.trim() +
				SEPARATOR +
				tagName.toLowerCase();

			// Use the tag name if it is a single element
		} else if (
			isSingleElementOrMatchingSiblings( selector, bestSelector, tagName )
		) {
			selector =
				(
					selector.match(
						new RegExp(
							'^(.*?)(?=' + selectorRegex( bestSelector ) + '$)',
							'g'
						)
					) || []
				)
					.join( '' )
					.toString()
					.trim() +
				SEPARATOR +
				tagName.toLowerCase();
		}
	}

	// Remove multiple spaces from the selector
	selector = spaceCleaner( selector );
	selector = cleanUnnecessaryGreaterThan( selector );

	// Prepare the selector for use
	selector = multipleVariation( spaceCleaner( selector ) );

	// Use the single selector if the selector is 'div' and there are more than 20 matches
	if (
		/( |>)div$/g.test( selector ) &&
		querySelectorAll( selector ).length >= 20
	) {
		return singleSelector( selector );
	}

	// Return the final result
	return selector;
}

/**
 * Checks if the given selector matches a single element or if elements
 * with the same parent and similar characteristics exist.
 *
 * @param {string} selector     - The selector to be matched.
 * @param {string} lastSelector - The last selector in the chain.
 * @param {string} tag          - The tag name to be checked.
 * @return {boolean} True if conditions are met, otherwise false.
 */
function isSingleElementOrMatchingSiblings(
	selector: string,
	lastSelector: string,
	tag: string
): boolean {
	const element = querySelectorAll( selector );
	const element2 = querySelectorAll(
		(
			selector.match(
				new RegExp(
					'^(.*?)(?=' + selectorRegex( lastSelector ) + '$)',
					'g'
				)
			) || []
		)
			.join( '' )
			.toString()
			.trim() +
			SEPARATOR +
			tag.toLowerCase()
	);

	// If both selectors match a single element
	if ( element.length === 1 && element2.length === 1 ) {
		return true;
	}

	// If the number of matched elements is the same and lastSelector contains class or ID selector
	if ( element.length === element2.length && /.|#/g.test( lastSelector ) ) {
		// Arrays to store depth and tag information
		const depthArray: number[] = [];
		const tagArray: string[] = [];

		document
			.querySelectorAll( lastSelector )
			.forEach( ( currentElement ) => {
				// Push depth of current element
				depthArray.push(
					currentElement.parentElement?.parentElement?.children
						.length || 0
				);

				// Push tag name of current element
				tagArray.push( currentElement.nodeName );
			} );

		const depthArrayEquals = depthArray.every(
			( v, _, a ) => v === a[ 0 ]
		);
		const tagArrayEquals = tagArray.every( ( v, _, a ) => v === a[ 0 ] );

		// Check if all elements have the same depth and tag
		if ( depthArrayEquals && tagArrayEquals ) {
			return true;
		}
	}

	return false;
}

function isDefined( a: unknown ): a is Exclude< typeof a, undefined > {
	return (
		typeof a !== typeof undefined &&
		a !== false &&
		a !== '' &&
		a !== ' ' &&
		a !== 'undefined' &&
		a !== null
	);
}

function isUndefined( a: unknown ): boolean {
	return (
		typeof a === typeof undefined ||
		a === false ||
		a === '' ||
		a === ' ' ||
		a === 'undefined' ||
		a === null
	);
}

function checkSelector( selector: string, mustHave: boolean ): boolean {
	try {
		const elements = querySelectorAll( selector );

		// Return false if document not have this element
		if ( mustHave && ! elements.length ) {
			// eslint-disable-next-line no-console
			console.log(
				"The selector doesn't match any element on this page"
			);
			return false;
		}

		return true;
	} catch ( e ) {
		// eslint-disable-next-line no-console
		console.log( e );
		return false;
	}
}

let isRecursiveCallForInputsEnabled = true;

/**
 * Determines the best CSS selector for a given element.
 *
 * This function evaluates an element's ID, classes, and tag name to return the
 * most specific and reliable selector. It filters out blocked and unpreferred
 * class names and considers numeric classes when necessary.
 *
 * @param {Element} element - The element to analyze.
 * @param {string}  mode    - The mode of selection, either 'single' or 'multiple'.
 * @return {string} - The best selector for the given element.
 */
function determineBestSelector(
	element: Element,
	mode: 'single' | 'multiple'
): string {
	const numberRegex = /\d+/;
	const tagName = element.tagName.toLowerCase();

	if ( tagName === 'body' || tagName === 'html' ) {
		return tagName;
	}

	// If element has a valid ID, return it as the selector.
	const elementId = element.id.trim();
	if (
		isDefined( elementId ) &&
		( ! numberRegex.test( elementId ) ||
			allowedDigitalIds.some( ( adi ) =>
				filterTest( adi, elementId, mode )
			) ) &&
		! blockedIds.some( ( bid ) => filterTest( bid, elementId, mode ) )
	) {
		return `#${ elementId }`;
	}

	// Clean and filter class names
	const className = cleanClassNameAttribute( element.className );
	const filteredClasses = applyFilter( blockedClasses, className ).trim();
	const refinedClasses = applyFilter( unPreferedClasses, filteredClasses );

	// Convert class lists to arrays
	let filteredClassArray = getClassesArray( filteredClasses );
	let refinedClassArray = getClassesArray( refinedClasses );

	// Extract numeric classes that are not in blocked lists
	const numericClassArray = refinedClassArray.filter(
		( cls ) =>
			numberRegex.test( cls ) &&
			! filterNumSkipBest.some( ( skip ) =>
				filterTest( skip, cls, mode )
			) &&
			! blockedDigitalClasses.some( ( bdc ) =>
				filterTest( bdc, cls, mode )
			)
	);

	// Remove numeric classes from main class lists
	filteredClassArray = filteredClassArray.filter(
		( cls ) => ! numberRegex.test( cls )
	);
	refinedClassArray = refinedClassArray.filter(
		( cls ) => ! numberRegex.test( cls )
	);

	let bestSelector;
	let preferredClassFound = false;

	// Look for preferred class in refined list
	refinedClassArray.find( ( cls ) =>
		preferedClasses.some( ( preferredCls ) => {
			if ( filterTest( preferredCls, cls, mode ) ) {
				if (
					cls === 'post' &&
					refinedClassArray.includes( 'hentry' )
				) {
					return false;
				}
				preferredClassFound = true;
				bestSelector = `.${ cls }`;
				return true;
			}
			return false;
		} )
	);

	// If no match, check initial filtered classes
	if ( ! preferredClassFound ) {
		filteredClassArray.find( ( cls ) =>
			preferedClasses.some( ( preferredCls ) => {
				if ( filterTest( preferredCls, cls, mode ) ) {
					preferredClassFound = true;
					bestSelector = `.${ cls }`;
					return true;
				}
				return false;
			} )
		);
	}

	// Check for preferred numeric class
	numericClassArray.find( ( numCls ) =>
		preferedDigitalClasses.some( ( preferredNumCls ) => {
			if ( filterTest( preferredNumCls, numCls, mode ) ) {
				bestSelector = `.${ numCls }`;
				return true;
			}
			return false;
		} )
	);

	// Handle input elements by type
	if ( tagName === 'input' && isRecursiveCallForInputsEnabled ) {
		const inputType = element.getAttribute( 'type' );
		if ( mode === 'single' && isDefined( inputType ) ) {
			isRecursiveCallForInputsEnabled = false;
			bestSelector =
				`input[type=${ inputType }]` +
				determineBestSelector( element, mode );
			isRecursiveCallForInputsEnabled = true;
		} else if ( isDefined( inputType ) ) {
			bestSelector = `input[type=${ inputType }]`;
		}
	}

	const isPreferredTag = simpleLikedTags.includes( tagName )
		? tagName
		: false;

	// If no class match, try a numeric class as last resort
	const lastResortNumeric = numericClassArray.length
		? getMostSpecificClass( numericClassArray )
		: false;

	// Find least occurring class for single inspector
	let rareClass: string | false = false;
	if ( mode === 'single' ) {
		rareClass = findLeastOccurringClass(
			getMostSpecificClass( refinedClassArray, true, false ) || []
		);
		if ( ! rareClass ) {
			rareClass = findLeastOccurringClass( refinedClassArray );
		}
		if ( ! rareClass ) {
			rareClass = findLeastOccurringClass(
				getMostSpecificClass( filteredClassArray, true, false ) || []
			);
		}
		if ( ! rareClass ) {
			rareClass = findLeastOccurringClass( filteredClassArray );
		}
	}

	const selectedClass =
		getMostSpecificClass( refinedClassArray, false, false ) ||
		getMostSpecificClass( filteredClassArray, false, false );

	// Final decision on the best selector
	if ( bestSelector && isDefined( bestSelector ) ) {
		return bestSelector;
	}
	if ( rareClass ) {
		return rareClass;
	}
	if ( selectedClass && ! Array.isArray( selectedClass ) ) {
		return selectedClass;
	}
	if ( isPreferredTag ) {
		return isPreferredTag;
	}
	if ( lastResortNumeric && ! Array.isArray( lastResortNumeric ) ) {
		return lastResortNumeric;
	}
	if ( tagName && tagName !== 'div' ) {
		return tagName;
	}
	if ( filteredClasses ) {
		let mostSpecific = getMostSpecificClass( filteredClassArray );
		if ( Array.isArray( mostSpecific ) || ! mostSpecific ) {
			mostSpecific = '';
		}
		const isBlocked = blockedDigitalClasses.some( ( bdc ) =>
			filterTest( bdc, mostSpecific.replace( /^(.|#)/g, '' ), mode )
		);
		return isBlocked ? tagName : mostSpecific;
	}

	return tagName;
}

function singleSelector( selector: string, test?: boolean ): string {
	let selectorArray = getSelectorArray( selector );
	let selectorPlus = '';

	// Loop through the selector array and build the selector progressively.
	for ( let i = 0; i < selectorArray.length; i++ ) {
		selectorPlus += ( i > 0 ? SEPARATOR : '' ) + selectorArray[ i ];

		// Only proceed if more than one element matches the current selector.
		const currentElementsCount = querySelectorAll( selectorPlus ).length;
		if ( currentElementsCount > 1 ) {
			querySelectorAll( selectorPlus ).forEach( ( element ) => {
				if ( ! selectorPlus.endsWith( ')' ) && element.parentElement ) {
					let indexOf = 0;
					Array.from( element.parentElement.children ).forEach(
						( child ) => {
							indexOf++;
							if (
								child.classList.contains( CUSTOM_CLASS ) ||
								child.querySelector( `.${ CUSTOM_CLASS }` )
							) {
								selectorPlus += `:nth-child(${ indexOf })`;
							}
						}
					);
				}
			} );
		}
	}

	// Clean up unnecessary :nth-child selectors.
	if ( selectorPlus.includes( ':nth-child' ) ) {
		selectorArray = getSelectorArray( selectorPlus );

		// Iterate over the selector array to clean up any unnecessary :nth-child.
		for ( let i = 0; i < selectorArray.length; i++ ) {
			const prevAll = getPreviousItem( selectorArray, i ).join( ' ' );
			const nextAll = getNextItem( selectorArray, i ).join( ' ' );
			let selectorPlusNew = `${ prevAll } ${ (
				selectorArray[ i ] as string
			 ).replace( /:nth-child\((.*?)\)/i, '' ) } ${ nextAll }`;
			selectorPlusNew = spaceCleaner( selectorPlusNew );

			// If only one element matches the selector without :nth-child, remove it.
			if ( querySelectorAll( selectorPlusNew ).length === 1 ) {
				selectorArray[ i ] = ( selectorArray[ i ] as string ).replace(
					/:nth-child\((.*?)\)/i,
					''
				);
			}
		}

		// Rebuild the selector without unnecessary :nth-child.
		selectorPlus = spaceCleaner( selectorArray.join( ' ' ) );
	}

	// Return the result based on the 'test' flag.
	if ( test ) {
		return selectorPlus;
	}

	// Apply children support and return the final result.
	return multipleVariation( addChildrenSupport( selectorPlus ) );
}

/**
 * Crops selector until it has 5 class or less
 *
 * @param selector
 * @return string
 */
function cropSelector( selector: string ): string {
	// Get the array representation of the selector.
	const selectorArray = spaceCleaner( selector )
		.replace( / *> */g, '>' )
		.split( ' ' );

	// If the selector array length is "short enough", use it.
	if ( selectorArray.length <= 5 ) {
		return selector;
	}

	// Otherwise, (try to) crop it.
	const initialElements = querySelectorAll( selector );

	let croppedArray = [ ...selectorArray ];
	let wasCropped = false;
	for ( let i = 0; i < selectorArray.length; ++i ) {
		const candidateArray = [ ...croppedArray ];
		candidateArray[ i ] = '';

		const candidateSelector = candidateArray.join( ' ' );
		const candidateElements = querySelectorAll( candidateSelector );

		if ( areEqual( initialElements, candidateElements ) ) {
			croppedArray = candidateArray;
			wasCropped = true;
		}
	}

	return wasCropped
		? croppedArray.join( ' ' ).replaceAll( '>', ' > ' )
		: selector;
}

/**
 * Attempts to simplify a complex CSS selector by generating variations
 * that are shorter but still select the same set of elements.
 *
 * @param {string} selector - The original CSS selector to simplify.
 * @return {string} - A potentially shorter, yet equivalent CSS selector.
 */
function multipleVariation( selector: string ): string {
	// Get the number of elements that match the original selector
	const originalSelectorLength = querySelectorAll( selector ).length;

	// Store the possibly cropped version of the original selector
	const originalSelector = cropSelector( selector );

	// Array to hold potential simplified selectors
	const variationSelectors: string[] = [];

	let firstSelectorPart: string | undefined;
	let lastSelectorPart: string | undefined;
	let combinedSelector = '';
	let middleSelectorParts: string[] = [];

	// Check if the selector contains '>' (child combinator)
	if ( ! selector.includes( '>' ) ) {
		// Split the selector into its individual parts
		middleSelectorParts = getSelectorArray( selector );

		// Extract the last element and remove it from the array
		lastSelectorPart = middleSelectorParts.pop();

		// Extract the first element and remove it from the array
		firstSelectorPart = middleSelectorParts.shift();

		// If the selector contains '>' symbols, handle accordingly
	} else {
		let selectorParts: string[] = [];
		let firstRegex: RegExp;
		let lastRegex: RegExp;
		let centerSelectorParts: string[] | undefined;
		let centerMatch: string;
		let type: 'getStart' | 'getEnd' | null = null;

		// Match for getting the last part after the last '>'
		const getEnd = ( selector.match( /\>(?!.*\>)(.*?)$/g ) ?? '' )
			.toString()
			.replace( /(\s)?>(\s)?/, '' );

		// Match for getting the first part before the first '>'
		const getStart = ( selector.match( /^(.*?)\s\>/g ) ?? '' )
			.toString()
			.replace( /(\s)?>(\s)?/, '' );

		// Determine if '>' is at the start or end
		if ( ! getEnd.includes( ' ' ) ) {
			selectorParts = getStart.split( ' ' );
			type = 'getStart';
		} else if ( ! getStart.includes( ' ' ) ) {
			selectorParts = getEnd.split( ' ' );
			type = 'getEnd';
		}

		// If type is still null, decide based on the length of matches
		if ( type === null ) {
			if ( getEnd.split( ' ' ).length > getStart.split( ' ' ).length ) {
				selectorParts = getEnd.split( ' ' );
				type = 'getEnd';
			} else {
				selectorParts = getStart.split( ' ' );
				type = 'getStart';
			}
		}

		// Cache first and last selectors and remove them from the array
		if ( selectorParts.length - 2 > 1 ) {
			firstSelectorPart = selectorParts[ 0 ];
			lastSelectorPart = selectorParts[ selectorParts.length - 1 ];

			// Regular expressions for parsing the selector parts
			firstRegex = new RegExp( '^(.*?) > ' + firstSelectorPart, 'g' );
			lastRegex = new RegExp( lastSelectorPart + ' > (.*?)$', 'g' );

			if (
				selector.match( firstRegex ) !== null &&
				selector.match( lastRegex ) !== null
			) {
				// Update first and last based on the type
				if ( type === 'getEnd' ) {
					firstSelectorPart = (
						selector.match( firstRegex ) ?? ''
					).toString();
				} else if ( type === 'getStart' ) {
					lastSelectorPart = (
						selector.match( lastRegex ) ?? ''
					).toString();
				}

				// Remove first and last to get the center selector parts
				selectorParts.pop();
				selectorParts.shift();

				centerSelectorParts = selectorParts;
			}
		}

		// Try another method to get the center of the selector if needed
		if ( centerSelectorParts === undefined ) {
			// Test if there are multiple '>' symbols
			if ( /\>(.*?)\>/g.test( selector ) ) {
				// Get the center part between '>' symbols
				centerMatch = ( selector.match( /\>(.*?)\>/g ) ?? '' )
					.toString()
					.replace( /(\s)?>(\s)?/g, '' );

				// Proceed if there's a space in the centerMatch
				if ( centerMatch.includes( ' ' ) ) {
					selectorParts = centerMatch.split( ' ' );

					// Cache first and last selectors and remove them from the array
					if ( selectorParts.length - 2 > 1 ) {
						firstSelectorPart = selectorParts[ 0 ];
						lastSelectorPart =
							selectorParts[ selectorParts.length - 1 ];

						// Regular expressions for parsing the selector parts
						firstRegex = new RegExp(
							'^(.*?) > ' + firstSelectorPart,
							'g'
						);
						lastRegex = new RegExp(
							lastSelectorPart + ' > (.*?)$',
							'g'
						);

						// Update first and last selectors
						firstSelectorPart = (
							selector.match( firstRegex ) ?? ''
						).toString();
						lastSelectorPart = (
							selector.match( lastRegex ) ?? ''
						).toString();

						// Remove first and last to get the center selector parts
						selectorParts.pop();
						selectorParts.shift();
						centerSelectorParts = selectorParts;
					}
				}
			}
		}

		// If a center selector was found, set middleSelectorParts to it
		if ( centerSelectorParts !== undefined ) {
			middleSelectorParts = centerSelectorParts;
		} else {
			// If no center selector is found, return the original selector
			return originalSelector;
		}
	}

	// Attempt to remove structural selectors from middleSelectorParts
	const filteredSelectors: string[] = [];
	let isValidSelector: boolean;
	let regex: RegExp;

	for ( let i = 0; i < middleSelectorParts.length; i++ ) {
		isValidSelector = true;

		for ( let j = 0; j < unPreferedSelectors.length; j++ ) {
			// Create a regex to test for unpreferred selectors
			regex = new RegExp(
				'(\\s|^).' + unPreferedSelectors[ j ] + '(\\s|$)',
				'gi'
			);

			// If the selector matches an unpreferred selector, mark as invalid
			if ( regex.test( middleSelectorParts[ i ] as string ) ) {
				isValidSelector = false;
				break;
			}
		}

		// If the selector passed all checks, add it to filteredSelectors
		if ( isValidSelector ) {
			filteredSelectors.push( middleSelectorParts[ i ] as string );
		}
	}

	// If first or last selector part is undefined, return the original selector
	if ( isUndefined( firstSelectorPart ) || isUndefined( lastSelectorPart ) ) {
		return originalSelector;
	}

	// Try combining first and last selectors if they don't contain spaces
	if (
		! firstSelectorPart?.includes( ' ' ) &&
		! lastSelectorPart?.includes( ' ' )
	) {
		combinedSelector = spaceCleaner(
			firstSelectorPart + SEPARATOR + lastSelectorPart
		);
	}

	// Check if the combined first and last selector is valid
	if ( checkSelector( combinedSelector, false ) ) {
		// If there were many structural selectors but none left after filtering
		// and the combined selector matches the original number of elements
		if (
			middleSelectorParts.length >= 1 &&
			filteredSelectors.length === 0 &&
			querySelectorAll( combinedSelector ).length ===
				originalSelectorLength
		) {
			return combinedSelector;
		}
	}

	// Update middleSelectorParts only if filteredSelectors has more than one element
	if ( filteredSelectors.length > 1 ) {
		middleSelectorParts = filteredSelectors;
	}

	// Require a minimum of 2 selectors (excluding first and last)
	if ( middleSelectorParts.length < 2 ) {
		return originalSelector;
	}

	// Filter middleSelectorParts to include only classes used at the same depth and tag names
	let depthList: number[] = [];
	let tagNameList: string[] = [];
	const consistentSelectors: string[] = [];

	for ( let i = 0; i < middleSelectorParts.length; i++ ) {
		if ( /.|#/g.test( middleSelectorParts[ i ] as string ) ) {
			depthList = [];
			tagNameList = [];

			// For each element matching the current selector
			document
				.querySelectorAll( middleSelectorParts[ i ] as string )
				.forEach( ( element ) => {
					depthList.push( getParents( element ).length );
					tagNameList.push( element.nodeName );
				} );

			// Check if all depths are equal
			const isDepthConsistent = depthList.every(
				( value, _, array ) => value === array[ 0 ]
			);

			// Check if all tag names are equal
			const isTagNameConsistent = tagNameList.every(
				( value, _, array ) => value === array[ 0 ]
			);

			// If both depth and tag name are consistent, add to consistentSelectors
			if ( isDepthConsistent && isTagNameConsistent ) {
				consistentSelectors.push( middleSelectorParts[ i ] as string );
			}
		}
	}

	// Update middleSelectorParts only if consistentSelectors has more than one element
	if ( consistentSelectors.length > 1 ) {
		middleSelectorParts = consistentSelectors;
	}

	// Variables for combining selectors
	const remainingSelectors = middleSelectorParts.slice( 0 );
	let currentSelector: string;
	let nextSelector: string;
	let testSelector: string;

	// Combine selectors to generate variations
	for ( let i = 0; i < middleSelectorParts.length; i++ ) {
		currentSelector = middleSelectorParts[ i ] as string;

		// Create a test selector with first, current, and last
		testSelector =
			firstSelectorPart +
			SEPARATOR +
			currentSelector +
			SEPARATOR +
			lastSelectorPart;

		// If the test selector matches the original number of elements, add to results
		if (
			querySelectorAll( testSelector ).length === originalSelectorLength
		) {
			variationSelectors.push( testSelector );
		}

		// Remove the current selector from remainingSelectors
		remainingSelectors.shift();

		// Combine current selector with the remaining selectors in remainingSelectors
		for ( let j = 0; j < remainingSelectors.length; j++ ) {
			nextSelector = remainingSelectors[ j ] as string;

			// Create a test selector with first, current, next, and last
			testSelector =
				firstSelectorPart +
				SEPARATOR +
				currentSelector +
				SEPARATOR +
				nextSelector +
				SEPARATOR +
				lastSelectorPart;

			// If the test selector matches the original number of elements, add to results
			if (
				querySelectorAll( testSelector ).length ===
				originalSelectorLength
			) {
				variationSelectors.push( testSelector );
			}
		}
	}

	// If no variations were found, return the original selector
	if ( variationSelectors.length === 0 ) {
		return originalSelector;
	}

	// Sort the results by selector length (shortest first)
	variationSelectors.sort( ( a, b ) => a.length - b.length );

	// Return the shortest valid selector variation
	return spaceCleaner( variationSelectors[ 0 ] );
}

function getMostSpecificClass(
	classes: string[],
	array: true,
	alternative?: boolean
): string[] | false;
function getMostSpecificClass(
	classes: string[],
	array?: false,
	alternative?: boolean
): string | false;
function getMostSpecificClass(
	classes: string[],
	array: boolean = false,
	alternative: boolean = false
) {
	// If there are no classes, return false.
	if ( classes.length === 0 ) {
		return false;
	}

	// If there's only one class and alternative is true, return false.
	if ( classes.length === 1 && alternative ) {
		return false;
	}

	// If there's only one class and alternative is false, return it.
	if ( classes.length === 1 && ! alternative ) {
		const singleClass = classes[ 0 ];
		if ( singleClass && singleClass.length > 1 ) {
			return array ? classes : `.${ singleClass }`;
		}
		return false;
	}

	// Filter out classes with special characters
	let filteredClasses = classes.filter( ( c ) => ! /[\[\]#,]/g.test( c ) );

	// Validate consistency in parent depth and tag names
	filteredClasses = filteredClasses.filter( ( c ) => {
		const depthArray: number[] = [];
		const tagArray: string[] = [];

		querySelectorAll( `.${ c }` ).forEach( ( element ) => {
			depthArray.push( getParents( element ).length );
			tagArray.push( element.nodeName );
		} );

		const sameDepth = depthArray.every(
			( depth ) => depth === depthArray[ 0 ]
		);
		const sameTags = tagArray.every( ( tag ) => tag === tagArray[ 0 ] );

		return sameDepth && sameTags;
	} );

	// If no valid classes remain, revert to the original list
	if ( filteredClasses.length === 0 ) {
		filteredClasses = classes;
	}

	// Separate classes with hyphens and underscores
	const hyphenatedClasses = filteredClasses.filter(
		( c ) => ! /_/.test( c ) && /-/.test( c )
	);
	const underscoredClasses = filteredClasses.filter(
		( c ) => ! /-/.test( c ) && /_/.test( c )
	);

	let selectedClasses: string[];
	if ( hyphenatedClasses.length > 0 ) {
		selectedClasses = hyphenatedClasses;
	} else if ( underscoredClasses.length > 0 ) {
		selectedClasses = underscoredClasses;
	} else {
		selectedClasses = filteredClasses;
	}

	// Remove classes with repeated characters (e.g., "tessst")
	let finalClasses = selectedClasses.filter( ( c ) => ! /(.)\1\1/.test( c ) );

	// If all classes were removed, revert to the previous selection
	if ( finalClasses.length === 0 ) {
		finalClasses = selectedClasses;
	}

	// Sort classes by length (longest first)
	finalClasses.sort( ( a, b ) => b.length - a.length );

	// Return the result in the requested format
	return array ? finalClasses : `.${ finalClasses[ 0 ] }`;
}

/**
 * Adds > symbol to single_selector.
 *
 * @param selector
 * @return string
 */
function addChildrenSupport( selector: string ): string {
	const selectorArray = getSelectorArray( selector );
	let newSelector = '';

	for ( const s of selectorArray ) {
		// Remove :nth-child() to prevent issues
		const currentSelector = s.replace( /:nth-child\((.*?)\)/i, '' );

		// Check if the same selector exists within the selector itself
		const testSelector =
			`${ newSelector } ${ currentSelector } ${ currentSelector }`.trim();

		// Append selector with ">" or space based on existence check
		const isAlreadySelected = querySelectorAll( testSelector ).length > 0;
		const link = isAlreadySelected ? '>' : ' ';
		newSelector += ` ${ link } ${ s } `;
	}

	newSelector = spaceCleaner( newSelector.trim() );

	// If selector still selects multiple elements, enforce ">" at the end
	if ( querySelectorAll( newSelector ).length > 1 ) {
		newSelector = newSelector.replace( /(?=[^ ]*$)/i, ' > ' );
	}

	newSelector = cleanUnnecessaryGreaterThan( newSelector );

	return spaceCleaner( newSelector );
}

function getSelectorArray( selector: string ) {
	// Clean up selector
	selector = selector
		.trim()
		.replace( /\s\s+/g, ' ' ) // Remove extra spaces
		.replace( /(\s)?([>+~,])(\s)?/g, '$2' ) // Remove spaces around ">,+,~"
		.replace( />/g, ' ' ) // Convert '>' to space
		.trim();

	// Split by space if there are multiple selectors
	const selectorArray = selector.length
		? selector.split( ' ' ).map( ( v ) => v.trim() )
		: [];

	// Add spaces around '~' and '+' and return the result
	return selectorArray.map( ( v ) =>
		v.replace( /\~/g, ' ~ ' ).replace( /\+/g, ' + ' )
	);
}

function spaceCleaner( data: string | undefined ) {
	return ( data || '' ).replace( /\s+/g, ' ' ).trim();
}

function filterTest(
	filter: string,
	className: string,
	mode: 'single' | 'multiple'
) {
	const singleInspectorPattern =
		mode === 'single' ? /\(\!singleInspector\)/g : /\(singleInspector\)/g;

	if ( singleInspectorPattern.test( filter ) ) {
		return false;
	}

	filter = filter.replace( singleInspectorPattern, '' );
	const regex = new RegExp( `(\\s|^)${ filter }(\\s|$)`, 'gi' );
	return regex.test( className );
}

function applyFilter( filter: string[], classes: string ) {
	classes = ` ${ classes } `.replaceAll( ' ', '  ' );
	filter.forEach( ( f ) => {
		const regex = new RegExp( '(\\s|^)' + f + '(\\s|$)', 'gi' );
		classes = classes.replace( regex, ' ' );
	} );

	return spaceCleaner( classes );
}

const INVALID_CHARS_REGEX =
	/([\u2018\u2019\u201A\u201B\u2032\u2035\u201C\u201D]|\{|\}|\:|\<|\>|\(|\)|\[|\]|\~|\"|\'|\?|\\)/g;
const ESCAPABLE_CHARS_REGEX = /(@|\.|\/|!|\*|#|\+)/g;
function cleanClassNameAttribute( value: unknown ): string {
	return ( 'string' === typeof value ? value : '' )
		.trim()
		.replace( /\s\s+/g, ' ' )
		.split( ' ' )
		.filter(
			( className ) => ! INVALID_CHARS_REGEX.test( className.trim() )
		)
		.map( ( className ) => className.trim() )
		.join( ' ' )
		.replace( ESCAPABLE_CHARS_REGEX, '\\$1' );
}

function getClassesArray( classes: string ) {
	return classes.trim().replace( /\s+/g, ' ' ).split( ' ' ).filter( Boolean );
}

/**
 * Finds the class with the least number of elements in the document.
 *
 * @param {string[]} classes - Array of class names to be checked.
 * @return {string|false} The class name with the least occurrences if it appears only once, otherwise false.
 */
function findLeastOccurringClass( classes: string[] ): string | false {
	const matchlessClasses = classes.sort(
		( b, a ) =>
			document.body.querySelectorAll( '.' + b ).length -
			document.body.querySelectorAll( '.' + a ).length
	);

	return document.body.querySelectorAll( '.' + matchlessClasses[ 0 ] )
		.length === 1
		? '.' + matchlessClasses[ 0 ]
		: false;
}

/**
 * Check if two selectors perform the same selection.
 *
 * @param selector
 * @param selectorNew
 */
function comparisonSelector( selector: string, selectorNew: string ): boolean {
	// Find elements matching the first selector
	const elements = querySelectorAll( selector );
	elements.forEach( ( el ) => el.classList.add( 'test-selector1-group' ) );

	// Find elements matching the second selector that also have the test class
	const elementsNew = querySelectorAll( selectorNew ).filter( ( el ) =>
		el.classList.contains( 'test-selector1-group' )
	);

	// Remove the test class after comparison
	elements.forEach( ( el ) => el.classList.remove( 'test-selector1-group' ) );

	// Ensure both selectors return the same number of elements
	if ( elements.length !== elementsNew.length ) {
		return false;
	}

	// Check if one selector contains ".CUSTOM_CLASS" while the other does not
	if (
		selector.includes( `.${ CUSTOM_CLASS }` ) !==
		selectorNew.includes( `.${ CUSTOM_CLASS }` )
	) {
		return false;
	}

	// Check for specific pseudo-classes in both selectors
	const pseudoClasses = [
		'active',
		'checked',
		'disabled',
		'empty',
		'enabled',
		'hover',
		'focus',
		'in-range',
		'invalid',
		'lang',
		'link',
		'optional',
		'out-of-range',
		'valid',
		'visited',
	];

	for ( const pseudo of pseudoClasses ) {
		const regex = new RegExp( `:${ pseudo }`, 'g' );
		if ( regex.test( selector ) !== regex.test( selectorNew ) ) {
			return false;
		}
	}

	return true;
}

function selectorRegex( selector: string ) {
	return selector
		.replace( /\\/g, '\\\\' ) // \
		.replace( /\./g, '\\.' ) // [
		.replace( /\[/g, '\\[' ) // [
		.replace( /\]/g, '\\]' ) // ]
		.replace( /\(/g, '\\(' ) // (
		.replace( /\)/g, '\\)' ) // )
		.replace( /\^/g, '\\^' ) // ^
		.replace( /\$/g, '\\$' ) // $
		.replace( /\*/g, '\\*' ) // *
		.replace( /\:/g, '\\:' ) // :
		.replace( /\+/g, '\\+' ); // +
}

function getPreviousItem( arr: unknown[], current: number ) {
	return arr.slice( 0, current );
}

function getNextItem( arr: unknown[], current: number ) {
	return arr.slice( current + 1 );
}

function getParents( element: Element ): Element[] {
	const parents: Element[] = [];
	let currentElement = element.parentElement;

	while ( currentElement ) {
		parents.push( currentElement );
		currentElement = currentElement.parentElement;
	}

	return parents;
}

function querySelectorAll( selector: string ) {
	try {
		return Array.from( document.querySelectorAll( selector ) );
	} catch ( _ ) {
		return [];
	}
}

function areEqual< T >(
	a: ReadonlyArray< T >,
	b: ReadonlyArray< T >
): boolean {
	return a.length === b.length && a.every( ( x ) => b.includes( x ) );
}

function cleanUnnecessaryGreaterThan( selector: string ) {
	if ( selector.indexOf( '>' ) === -1 ) {
		return selector;
	}

	const length = selector.split( '>' ).length;
	const initialElements = querySelectorAll( selector );

	for ( let i = 1; i < length; i++ ) {
		const candidate = selector.replace( / > /i, ' ' );
		const candidateElements = querySelectorAll( candidate );
		if ( areEqual( initialElements, candidateElements ) ) {
			selector = candidate;
		}
	}

	return selector;
}

/* ---------------------------------------*/
/* Prefer one digital class in 2 or more. */
/* ---------------------------------------*/
const preferedDigitalClasses = [
	// General
	'wp-image-[0-9]+',

	// Basic
	'([a-zA-Z0-9_-]+)?(section|slide|button|image|row)([a-zA-Z0-9_-]+)?',

	// Visual Composer
	'vc_custom_([a-zA-Z0-9_-]+)?',

	// Beaver Builder
	'fl-node([a-zA-Z0-9_-]+)?',
	'fl-row([a-zA-Z0-9_-]+)?',

	// Themify
	'module_row_([0-9]+)',
	'module_column_([0-9]+)',

	// Divi
	'et_pb_(section|row)_[0-9]+',

	// King Composer
	'kc-css-([0-9]+)',

	// Forge Builder
	'forge-col[0-9]+',

	// Enfold
	'(avia|av)-builder-el-([0-9]+)',

	// Flatsome
	'footer-([0-9]+)',

	// Other
	'gb-container-([a-zA-Z0-9_-]+)?',
	'wp-container-[0-9]+',
	'wp-elements-[0-9]+',
	'(n2-font-|n2-style-)([a-zA-Z0-9_-]+)?',
	'n2-ss-([0-9]+)item([0-9]+)',
	'n-uc-([a-zA-Z0-9_-]+)',
	'ms-parent-id-([0-9_-]+)',
	'msp-cn-([a-zA-Z0-9_-]+)',
	'e([0-9-]+)',
	'td_module_([0-9]+)',
	'cp_id_([a-zA-Z0-9_-]+)',
	'snp-pop-([0-9]+)',
	'dsgn__basic-([0-9]+)',
	'td_block_([0-9]+)',
	'weforms-([0-9]+)',
	'fusion-builder-row-([0-9]+)',
];

/* -----------------*/
/* Prefered Classes */
/* -----------------*/
const preferedClasses = [
	'(current-menu-item|post|hentry|widget|wp-post-image|comment-author-admin|next|prev|product|footer|footer-top|footer-bottom|header|navbar|sidebar|masthead|copyright|menu-item|form-control|row_inner|wpDataTable|ls-wrapper|x-column)',
	'active(!singleInspector)',
	'current(!singleInspector)',
	'(entry|article|post|page|item|widget)([-_])?title',
	'(entry|article|post|page|item|widget)([-_])?content',
	'(entry|article|post|page|item|widget)([-_])?meta',
	'([a-zA-Z0-9_-]+)?item',
	'kc-css-([a-zA-Z0-9_-]+)?',
	'filterall',
];

/* ---------------------------------------------------- */
/* These classes will never be used in the selector.    */
/* Use only if tag is "div" and not have a alternative. */
/* ---------------------------------------------------- */
const blockedClasses = [
	'nab-selected',
	'nab-focused-element',
	'nab-highlight',
	'nab-highlight__tooltip',
	'nab-highlight__tooltip-text',
	'nab-breadcrumb-highlight',
	'nab-hovered',
	'nab-hovered__tooltip',
	'nab-hovering',
	'nab-breadcrumbs-container',
	'nab-breadcrumbs-list',
	'nab-breadcrumbs-list-item',
	'nab-breadcrumbs-list-item__content',
	'nab-breadcrumbs-list-item--selected',

	// Elementor
	'([a-zA-Z0-9_-]+)?(elementor)([a-zA-Z0-9_-]+)?',
	'(e-)([a-zA-Z0-9_-]+)?',

	// Classes from a animate.css
	'([a-zA-Z0-9_-]+)?(infinite|bounce|pulse|rubberBand|shake|headShake|swing|wobble|jello|hinge|fade)([a-zA-Z0-9_-]+)?',
	'(([a-zA-Z0-9_-]+)?([-_]flash|flash[-_]|[-_]flash[-_])([a-zA-Z0-9_-]+)?|flash)',
	'(([a-zA-Z0-9_-]+)?([-_]tada|tada[-_]|[-_]tada[-_])([a-zA-Z0-9_-]+)?|tada)',
	'([a-zA-Z0-9_-]+)?slide([a-zA-Z0-9_-]+)?(in|out)([a-zA-Z0-9_-]+)?(up|down|left|right)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(roll|fall|flip|lightSpeed|rotate|zoom)([a-zA-Z0-9_-]+)?(in|out|up|down|left|right)([a-zA-Z0-9_-]+)?',

	'([a-zA-Z0-9_-]+)?slide([a-zA-Z0-9_-]+)?(In|in|Out|out)([a-zA-Z0-9_-]+)?(Up|up|Down|down|Left|left|Right|right)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?roll([a-zA-Z0-9_-]+)?(In|in|Out|out)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?fall([a-zA-Z0-9_-]+)?(In|in|Out|out|Up|up|Down|down|Left|left|Right|right)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?flip([a-zA-Z0-9_-]+)?(In|in|Out|out|Up|up|Down|down|Left|left|Right|right)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?lightSpeed([a-zA-Z0-9_-]+)?(In|in|Out|out)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?rotate([a-zA-Z0-9_-]+)?(In|in|Out|out)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?zoom([a-zA-Z0-9_-]+)?(In|in|Out|out)([a-zA-Z0-9_-]+)?',

	// Post Status classes
	'([a-zA-Z0-9_-]+)?(publish|draft|pending|private|trash)([a-zA-Z0-9_-]+)?',

	// Basic post formats
	'(standard|aside|audio|chat|gallery|image|link|quote|status|video)',

	// Some functional classes
	'([a-zA-Z0-9_-]+)?(viewport|ltr|padding|inherit|margin|relative|transparent|visibility|hidden|active-slide|current-slide|hide|animated|draggable|resize|cloned|sortable|status|clearfix|clear|(clr|clfw)|clean|hover|default_template|ready|validate|false|true|loading|loaded|finished|center|delay|enabled|disabled|ga-track|raw_code|raw_html|padded|bold|initialised|even|odd|dismissable|underlined|flippable|clickable|gutter|animation|animate|transition)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?-(spacing-yes|spacing-no)',
	'([a-zA-Z0-9_-]+)?([-_]drag[-_]|drag[-_]|[-_]drag|active-slide|current-slide|selected-slide|slide-active|slide-current|slide-selected|draggable|sortable|dismissable|flippable|clickable|focus|viewport|ltr|inherit|relative|absolute|transparent|visibility|spaced|hidden|hideresize|cloned|status|clean|clr|clfw|ready|validate|false|true|loading|loaded|added|move|moving|finished|delay|enabled|disabled|bold|italic|initialised|even|odd|underlined|gutter|animation|animating|animate|transition|repeat|cursor|pointer|uppercase|lowecase|all-caps|capitalize|padding|margin|padded|[_-])(to|from)([_-])(top|left|right|bottom)([a-zA-Z0-9_-]+)?',
	'((?!n2)([a-zA-Z0-9_-]+)hover([_-])([a-zA-Z0-9]+)?|hover|hover([_-])([a-zA-Z0-9]+)?|(?!n2)([a-zA-Z0-9_-]+)hover|hovered)',
	'(?!n2)([a-zA-Z0-9_-]+)[-_]font([a-zA-Z0-9_-]+)?',

	'(lang|language|translate)([-_])([a-zA-Z0-9_-]+)?',
	'([_-]?)([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3}|rgba|rgb)([_-]?)?',
	'([a-zA-Z0-9_-]+)?rand',
	'([a-zA-Z0-9_-]+)?(mrg|pdg|m|p|pad|mar|margin|pading|padng|mrn)([-_])(t|r|b|l|btm|top|left|right|tp|lft|rght|bottom)([-_])([a-zA-Z0-9_-]+)',
	'is([_-])([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)none',
	'([a-zA-Z0-9_-]+)?with([_-])([a-zA-Z0-9]+)',
	'(display[-_]?block|display[-_]?inline|display[-_]?inline[-_]?block)',
	'(bottom|top|left|right|center|align|middle|up|down)[-_](bottom|top|left|right|center|align|middle|up|down)',
	'(bottom|top|left|right|center|align|middle|up|down)[-_](bottom|top|left|right|center|align|middle|up|down)[-_](bottom|top|left|right|center|align|middle|up|down)',
	'(not|no)([_-])([a-zA-Z0-9_-]+)?',
	'(roboto|lato|montserrat|slabo|raleway|merriweather|poppins|lora|titillium[-_]?web|muli|arimo)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)[-_](serif|sans|display|monospace|webfont)([a-zA-Z0-9_-]+)?',
	'page([_-])item',
	'([a-zA-Z0-9_-]+)?object([_-])page',
	'([a-zA-Z0-9_-]+)?closed',
	'thread([_-])alt',
	'([a-zA-Z0-9_-]+)?([_-])has([_-])?([a-zA-Z0-9_-]+)|([a-zA-Z0-9_-]+)?([_-])?has([_-])([a-zA-Z0-9_-]+)',
	'screen([_-])reader([_-])text',
	'tag-link([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(internet([-_])?explorer|opera|firefox|safari|chrome|product_tag|product_cat)([a-zA-Z0-9_-]+)?',
	'(img-responsive|ls-active|disappear|appear|noSwipe|wow|bootstrap-touchspin-down|section--no|cat-item|kc-elm|kc_column|selected|alternate_color|open-mega-a|sf-menu|sf-arrows|ajax|neg-marg|lazy|lazyload|lazy-img|text-shadow-small|full|sort|elementor-column-gap-no|n2-ow|et_pb_css_mix_blend_mode_passthrough|filterall|notranslate|vce|bordb|master-slider-parent|ms-sl-selected|non-hundred-percent-height-scrolling|hundred-percent-fullwidth|post-no-media|ie|form-label-above)',
	'([a-zA-Z0-9_-]+)?((syle|style)-default|nojquery|js-comp-ver|wpb-js-composer|disable-responsive-headings|ut-vc-|default([-_])template|ga-track|raw([-_])code|raw([-_])html|withbg|bg([-_])layout)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?-shd',
	'([a-zA-Z0-9_-]+)?video-aspect-ratio-([a-zA-Z0-9_-]+)',
	'([a-zA-Z0-9_-]+)?([-_])spacing([-_])(no|yes)',
	'n2-(ow|ss)-(all|desktop|mobile|tablet)',
	'([a-zA-Z0-9_-]+)?show-dropdown',
	'page-section-[a-zA-Z0-9]{13,13}',

	// Functional 3 party classes
	'([a-zA-Z0-9_-]+)?withbg([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?bg_layout([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-s]+)?rand',
	'mrg-(btm|top|left|right|tp|lft|rght)-([a-zA-Z0-9_-]+)',
	'is([_-])([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)none',

	// WordPress Core
	'page([_-])item',
	'([a-zA-Z0-9_-]+)?object([_-])page',
	'([a-zA-Z0-9_-]+)?closed',
	'thread([_-])alt',
	'([a-zA-Z0-9_-]+)?([_-])has([_-])?([a-zA-Z0-9_-]+)|([a-zA-Z0-9_-]+)?([_-])?has([_-])([a-zA-Z0-9_-]+)',
	'screen([_-])reader([_-])text',
	'tag-link([a-zA-Z0-9_-]+)?',
	'post-no-media',

	// Browser Classes
	'([a-zA-Z0-9_-]+)?opera([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?firefox([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?safari([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?chrome([a-zA-Z0-9_-]+)?',

	// WooCommerce
	'([a-zA-Z0-9_-]+)?product_tag([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?product_cat([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?taxable([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?onsale([a-zA-Z0-9_-]+)?',
	'calculated_shipping',
	'currency([a-zA-Z0-9_-]+)?',
	'woocommerce-MyAccount-navigation-link--([a-zA-Z0-9_-]+)?',

	// Etc
	'img-responsive',
	'ls-active',
	'disappear',
	'appear',
	'noSwipe',
	'wow',
	'bootstrap-touchspin-down',
	'section--no',
	'cat-item',
	'([a-zA-Z0-9_-]+)?direction-ltr',
	'([a-zA-Z0-9_-]+)?show-dropdown',
	'kc-elm',
	'kc_column',
	'selected',
	'alternate_color',
	'open-mega-a',
	'sf-menu',
	'sf-arrows',

	'([a-zA-Z0-9_-]+)?nojquery([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?js-comp-ver([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?wpb-js-composer([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?-shd',
	'([a-zA-Z0-9_-]+)?with([_-])([a-zA-Z0-9]+)',
	'([a-zA-Z0-9_-]+)?m-t-([0-9])+([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(serif|sans|font|webfont)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?uppercase([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?([_-])(to|from)([_-])(top|left|right|bottom)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(cursor|pointer)([a-zA-Z0-9_-]+)?',
	'(not|no)([_-])([a-zA-Z0-9_-]+)?',
	'ajax',
	'neg-marg',
	'([a-zA-Z0-9_-]+)?video-aspect-ratio-([a-zA-Z0-9_-]+)',
	'lazy',
	'lazy-img',
];

/* ------------------------------------------*/
/* Never uses the following nummeric classes */
/* ------------------------------------------*/
const blockedDigitalClasses = [
	'([a-zA-Z0-9_-]+)?page([_-])item([_-])([0-9]+)',
	'(vc_|vc-)(.*?)(_|-)[a-zA-Z-0-9]{22,22}',
	'themify_builder_content-([0-9]+)',
	'themify_builder_([0-9]+)_row',
	'tb_([0-9]+)_column',
	'et_pb_image_([0-9]+)',
	'([a-zA-Z0-9_-]+)?(post|page|portfolio|product|work|port|form|video)([_-])([0-9]+)',
	'([a-zA-Z0-9_-]+)?(post|page|portfolio|product|work|port|form|video)([_-])(entry|item|id)([_-])([0-9]+)',
	'([0-9])+(px|em|rem)',
	'([a-zA-Z0-9_-]+)?wishlist-([0-9])+',
	'wpbs-bookable-([0-9])+',
	'wpbs-day-([0-9])+',
	'([a-zA-Z0-9_-]+)?rand-([0-9])+',
	'([a-zA-Z0-9_-]+)?(([-_])ie|ie8|ie9|ie10|ie11)',
	'testimonials-items-([a-zA-Z0-9_-]+)',
	'instance-([0-9]+)',
];

/* ------------------------------------------*/
/* Allow just the following nummeric ids     */
/* Ex: #section-15 is an allowed nummeric id */
/* ------------------------------------------*/
const allowedDigitalIds = [
	// General
	'([a-zA-Z0-9_-]+)?(module|slide|section|row|layout|form|wrapper|container|parallax|block)([a-zA-Z0-9_-]+)?',

	// 3rd party plugin allowed ids
	'layers-widget-([a-zA-Z0-9_-]+)?',
	'builder-module-([a-zA-Z0-9_-]+)?',
	'pg-([a-zA-Z0-9_-]+)?',
	'ptpb_s([a-zA-Z0-9_-]+)?',
	'billing_address_([0-9])([a-zA-Z0-9_-]+)?',
	'el-([a-zA-Z0-9_-]+)',
	'dslc-module-([a-zA-Z0-9_-]+)',
	'module-([0-9]){13,13}-([0-9]){4,4}',
	'wrapper-([0-9]){13,13}-([0-9]){4,4}',
	'eluid([a-zA-Z0-9_-]+)',
	'nf-form-([0-9]+)-cont',
	'nf-field-([0-9]+)-wrap',
	'ulp-inline-([a-zA-Z0-9_-]+)?',
	'ulp-layer-([a-zA-Z0-9_-]+)?',
	'ulp-([a-zA-Z0-9_-]+)?',
	'esg-grid-([0-9]+)-([0-9]+)',
	'snp-bld-step-([0-9]+)',
	'snp-bld-step-([0-9]+)-el-([0-9]+)',
	'snppopup-content-([0-9]+)',
	'gform_([0-9]+)',
];

const blockedIds = [
	'widget',
	'recentcomments',
	'fws_([a-zA-Z0-9_-]+)',
	'wrapper-[a-zA-Z-0-9]{16,16}',
	'rps_([a-zA-Z0-9_-]+)',
	'search-form-[a-zA-Z0-9]{13,13}',
	'fullwidth-block-[a-zA-Z0-9]{13,13}',
	'nf-field-([0-9]+)-container',
	'phantom',
	'wpforms-([0-9]+)-field_([0-9]+)([a-zA-Z0-9_-]+)?(!singleInspector)',
	'input_([0-9]+)_([0-9]+)_([0-9]+)_container(!singleInspector)',
	'gform_fields_([0-9]+)',
];

/* -------------------------------------------*/
/* Unprefered classes that are not a priority */
/* -------------------------------------------*/
const unPreferedClasses = [
	// Logical
	'([a-zA-Z0-9_-]+)([_-])',
	'([_-])([a-zA-Z0-9_-]+)',
	'([a-zA-Z0-9_-]+)?([_-])([_-])([a-zA-Z0-9_-]+)?', // multiple -_ ex: bad--class--name

	// WordPress Dynamic Classes
	'([a-zA-Z0-9_-]+)?([_-])([_-]|tag|category|cat)([_-]|format)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(tag|category|cat)([_-])([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?format([a-zA-Z0-9_-]+)?',
	'menu([_-])item([_-])type([_-])post([_-])type',
	'menu([_-])item([_-])object([_-])page',
	'menu([_-])item([_-])(object|type)([_-])custom',
	'widget_([a-zA-Z0-9_-]+)',
	'bg([-_])([a-zA-Z0-9_-]+)',

	// Modern Columns.
	'([a-zA-Z0-9_-]+)?([_-])(l|m|s|xs)([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?pure([_-])([a-zA-Z0-9_-]+)?([_-])u([_-])[0-9]+([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?col([_-])([a-zA-Z0-9_-]+)?([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?col([_-])([a-zA-Z0-9_-]+)?([_-])offset([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?(medium|large|small)([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?(medium|large|small)([_-])([a-zA-Z0-9_-]+)?([_-])[0-9]+',

	// Bootstrap Classes
	'(xs|s|m|l|xl|xsmall|small|medium|large|xlarge)([_-])(start|end|center|between)',
	'([a-zA-Z0-9_-]+)?(small|medium|large)([_-])(push|pull)([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?span[0-9]+',
	'([a-zA-Z0-9_-]+)?span([_-])?[0-9]+',
	'([a-zA-Z0-9_-]+)?col([_-])[0-9]+([_-])[0-9]+',
	'([a-zA-Z0-9_-]+)?col([_-])[0-9]+',

	// Classic Grid Columns
	'(column|columns|col)',
	'([a-zA-Z0-9_-]+)(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)([a-zA-Z0-9_-]+)',

	// Structural
	'([a-zA-Z0-9_-]+)?(sticky|fixed|logged|print|visible|trigger|required)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?pull([a-zA-Z0-9_-]+)(left|right)',
	'(left|right)',
	'([a-zA-Z0-9_-]+)?([_-])(yes|no)([_-]|yes|no)([_-]|([_-])(yes|no)[_-]|is([_-])active|clearfix|clear)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?([_-])(yes|no)([_-])([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(yes|no)([_-])([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?([_-])(yes|no)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?is([_-])active([a-zA-Z0-9_-]+)?',
	'(xsmall|x?[-_]small|small|large|medium|huge|normal)',
	'(?!n2-)([a-zA-Z0-9_-]+)style([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?first',
	'([a-zA-Z0-9_-]+)?last',
	'([a-zA-Z0-9_-]+)?text([_-])justify',
	'([a-zA-Z0-9_-]+)?effect([0-9_-]+)',

	// Dynamic CSS classes.
	'([a-zA-Z0-9_-]+)?(background|width|height|position|parent|color|layout|invert|scroll|equal|square|([_-])skin|skin([_-])|toggled|style([0-9_-]+)|rounded|radius|type|dynamic|row|border|align|dimension|inline-inside|corner-pointed|float|last-child|first-child|hide|show|parallax|responsive|intense|completed|with_ajax|no-sibling)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?first',
	'([a-zA-Z0-9_-]+)?last',
	'([a-zA-Z0-9_-]+)?text([_-])justify',
	'([a-zA-Z0-9_-]+)?align([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?effect([0-9_-]+)',
	'([a-zA-Z0-9_-]+)?dimension([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?inline-inside([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?corner-pointed([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(round|scale|shadow|rotate|default|minimal|anim|pos[-_]align|angled)([a-zA-Z0-9_-]+)?',

	// General Theme Option Classes
	'([a-zA-Z0-9_-]+)([_-])(on|off)',
	'([a-zA-Z0-9_-]+)default([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)size([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)mobile([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)desktop([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)populated([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?hide([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?show([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?parallax([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?responsive([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?intense([a-zA-Z0-9_-]+)?',

	// Unyson
	'unyson-page',
	'end',

	// Pagenavi
	'larger',
	'smaller',

	// Buddypress
	'created_group',
	'mini',
	'activity_update',

	// Force Builder
	'forge-block',
	'forge-',

	// Elementor
	'elementor-section-items-middle',

	// Themify
	'themify_builder',
	'module',

	// Live composer
	'dslc-post-no-thumb',

	// WooCommerce
	'downloadable',
	'purchasable',
	'instock',

	// Others
	'above',
	'open',

	// Enfold
	'template-page',
	'alpha',
	'units',
	'flex_column_div',
	'([a-zA-Z0-9_-]+)?no-sibling([a-zA-Z0-9_-]+)?',

	'([a-zA-Z0-9_-]+)?float([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(blue|black|red|dark|white|light|green|yellow|purple|pink|orange|brown|gray)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(black|dark|white|light)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?last-child([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?first-child([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)([_-])only',
	'([a-zA-Z0-9_-]+)?(text-left|text-right)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(round|scale|shadow|rotate|default|minimal|animsition|nimation)([a-zA-Z0-9_-]+)?',
	'([a-zA-Z0-9_-]+)?(text-left|text-center|text-right)([a-zA-Z0-9_-]+)?',
	'(unyson-page|end|larger|smaller|height-full|created_group|mini|activity_update|forge-block|forge-|elementor-section-items-middle|themify_builder|module|dslc-post-no-thumb|downloadable|purchasable|instock|above|open|template-page|alpha|units|flex_column_div|woocommerce|affix-top|st-menu|circle|vc_figure|vc_general|waves-effect|collapsed|collapse)',
	'woocommerce',
	'affix-top',
	'st-menu',
	'circle',
	'vc_figure',
	'vc_general',
	'waves-effect',
	'collapsed',
	'collapse',
];

/* ------------------------------------------------*/
/* Exclude the structural classes in the selector. */
/* ex: col-md-8, exclude it if no need.            */
/* ----------------------------------------------- */
const unPreferedSelectors = [
	'([a-zA-Z0-9_-]+)?(inner|overlay|container|google-fonts|fl-col-content)([a-zA-Z0-9_-]+)?',
	'elementor([a-zA-Z0-9_-]+)?(column|gap|wide|wrap)([a-zA-Z0-9_-]+)?',
	'ms-([a-zA-Z0-9_-]+)-view',
	'(kc-wrap-columns|dslc-modules-section-wrapper|av-content-full|ninja-forms-form-wrap|nf-fields-wrap|nf-field-element|tp-tab-mask|n2-ss-section-outer|sp-mask|sp-grab|ms-slide-layers|ls-layers|snp-fb|form_sublabel_below|gform_body)',
];

/* -----------------------------------------------*/
/* Process these nummeric classes as non nummeric */
/* -----------------------------------------------*/
const filterNumSkipBest = [ '([a-zA-Z0-9_-]+)?(wpcf7|mc4wp)([a-zA-Z0-9_-]+)?' ];

/* -------------------------- */
/* Prefered tags as selectors */
/* -------------------------- */
const simpleLikedTags = [
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'p',
	'span',
	'img',
	'strong',
	'a',
	'li',
	'i',
	'ul',
	'header',
	'footer',
	'article',
	'b',
	'em',
	'code',
	'form',
	'label',
	'ol',
	'small',
	'blockquote',
	'nav',
];

function maybeAddTag( tagName: string, selector: string | undefined ) {
	if ( ! selector ) {
		return undefined;
	}

	selector = spaceCleaner( selector );
	// eslint-disable-next-line @wordpress/no-unused-vars-before-return
	const [ last = '', ...parts ] = selector.split( ' ' ).reverse();
	if ( ! /^[^a-zA-Z]/.test( last ) ) {
		return selector;
	}

	tagName = tagName.toLowerCase();
	return [ `${ tagName }${ last }`, ...parts ].reverse().join( ' ' );
}
